"use client";

import { useEffect, useMemo, useState } from "react";
import {
  Checkbox,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_PRIMARY,
  BUTTON_SECONDARY,
  CARD,
  HINT,
  LABEL,
  WIDGET_H2,
} from "@/components/tool-kit/styles";
import {
  PacerText,
  ReadingStage,
} from "@/components/tools/study-more/reading-stage";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { formatClock } from "@/lib/tool-kit/study-more/noise";
import {
  chunkDelay,
  chunkIndexOfWord,
  chunkWords,
  estimateRun,
  MAX_WPM,
  MIN_WPM,
  tokenize,
  wordsBefore,
} from "@/lib/tool-kit/study-more/speed-reading";

type Mode = "rsvp" | "chunks" | "pacer";

const RANGE =
  "w-full cursor-pointer accent-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary";
const SIZES = ["2", "3", "4", "5"] as const;
const BACK_WORDS = 10;

/** English strings. A translated content file overrides these through `ui`. */
export const SPEED_READING_TRAINER_STRINGS = {
  textLabel: "Text to read",
  textPlaceholder: "Paste an article, a chapter or your notes here.",
  textHint:
    "Runs entirely in your browser: nothing is uploaded or stored. Up to 20,000 words.",
  useSample: "Use sample text",
  sampleText:
    "Water on Earth is always moving. The sun warms oceans, lakes and rivers, and some of that water evaporates and rises as invisible vapour. High in the atmosphere, where the air is cold, the vapour condenses into tiny droplets that gather into clouds. When the droplets grow heavy enough, they fall as rain, snow or hail. Some of this water runs over the ground and returns to rivers and the sea. Some soaks into the soil and is taken up by plants, which release it back into the air through their leaves. Some sinks deeper and becomes groundwater, which can stay underground for years or even thousands of years before it emerges at a spring. Together these steps form the water cycle, which has no real beginning or end.",
  modeLabel: "Display",
  modes: {
    rsvp: "One word at a time",
    chunks: "Short chunks",
    pacer: "Highlight in the text",
  },
  modeNotes: {
    rsvp: "Each word flashes in the same spot, anchored on one coloured letter. Rapid flashing can be tiring, and it removes your ability to glance back.",
    chunks:
      "Two to five words flash together, which is closer to how the eyes take in a line of text.",
    pacer:
      "The text stays still and a highlight moves through it at your pace, so you can still look back. Gentlest on the eyes.",
  },
  sizeLabel: "Words per flash",
  wpmLabel: "Speed",
  wpmUnit: "{wpm} words per minute",
  wpmHint:
    "For comparison, adults average about 238 words per minute reading non-fiction silently (Brysbaert, 2019).",
  pausesLabel: "Pause a little longer after commas and full stops",
  breakLabel: "Keep chunks within a clause or sentence",
  reducedMotion:
    "Your device asks for reduced motion, so the highlight display is selected. The flashing displays are still there if you want them.",
  start: "Start",
  pause: "Pause",
  resume: "Resume",
  restart: "Restart",
  back: "Back 10 words",
  positionLabel: "Position in the text",
  progress: "{current} of {total} words",
  placeholder: "Press Start",
  enterText: "Paste some text above, or use the sample, to begin.",
  runEstimate:
    "At this setting: {words} in {time}, an effective pace of {pace} words per minute.",
  wordCount: "one:{count} word|other:{count} words",
  doneHeading: "Finished",
  statWords: "Words",
  statTime: "Time on screen",
  statPace: "Pace set",
  recallHeading: "Check what you took in",
  recallLabel: "Without looking back, write what the text said",
  recallHint:
    "This is the part that counts. If you remember much less than at your normal reading speed, the pace is too fast for this text.",
  showOriginal: "Show the original text",
  hideOriginal: "Hide the original text",
  originalLabel: "Original text",
  caveat:
    "Faster presentation costs accuracy: researchers who reviewed the evidence found that readers are unlikely to double or triple their speed and understand as well as before (Rayner et al., 2016). Treat a higher pace as a way to skim, not to study.",
} as const;

function useReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    setReduced(window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  }, []);
  return reduced;
}

export default function SpeedReadingTrainerWidget() {
  const strings = useToolUi(SPEED_READING_TRAINER_STRINGS);
  const locale = useToolLocale();
  const fmt = useToolNumber();
  const reducedMotion = useReducedMotion();
  const [text, setText] = useState("");
  const [wpm, setWpm] = useState(300);
  const [mode, setMode] = useState<Mode>("rsvp");
  const [size, setSize] = useState<(typeof SIZES)[number]>("3");
  const [pauses, setPauses] = useState(true);
  const [breakAtPunctuation, setBreakAtPunctuation] = useState(true);
  const [playing, setPlaying] = useState(false);
  const [index, setIndex] = useState(0);
  const [finished, setFinished] = useState(false);
  const [recall, setRecall] = useState("");
  const [showOriginal, setShowOriginal] = useState(false);

  useEffect(() => {
    if (reducedMotion) {
      setMode("pacer");
    }
  }, [reducedMotion]);

  const words = useMemo(() => tokenize(text), [text]);
  const perChunk = mode === "rsvp" ? 1 : Number(size);
  const chunks = useMemo(
    () => chunkWords(words, perChunk, breakAtPunctuation),
    [words, perChunk, breakAtPunctuation]
  );
  const estimate = useMemo(
    () => estimateRun(chunks, wpm, pauses),
    [chunks, wpm, pauses]
  );
  const current = chunks[index] ?? null;
  const wordsRead = wordsBefore(chunks, index);

  useEffect(() => {
    if (!playing) {
      return;
    }
    const chunk = chunks[index];
    if (!chunk) {
      setPlaying(false);
      setFinished(true);
      return;
    }
    const id = setTimeout(
      () => setIndex((value) => value + 1),
      chunkDelay(chunk, wpm, pauses)
    );
    return () => clearTimeout(id);
  }, [playing, index, chunks, wpm, pauses]);

  function changeText(value: string) {
    setText(value);
    setPlaying(false);
    setIndex(0);
    setFinished(false);
    setRecall("");
    setShowOriginal(false);
  }

  /** Keeps the reader's place in the text when the chunking changes. */
  function relayout(nextMode: Mode, nextSize: string, nextBreak: boolean) {
    const nextChunks = chunkWords(
      words,
      nextMode === "rsvp" ? 1 : Number(nextSize),
      nextBreak
    );
    setIndex(chunkIndexOfWord(nextChunks, wordsRead));
  }

  function start() {
    if (finished || index >= chunks.length) {
      setIndex(0);
      setRecall("");
      setShowOriginal(false);
    }
    setFinished(false);
    setPlaying(true);
  }

  function restart() {
    setPlaying(false);
    setIndex(0);
    setFinished(false);
  }

  function back() {
    setIndex(chunkIndexOfWord(chunks, Math.max(0, wordsRead - BACK_WORDS)));
    setFinished(false);
  }

  const empty = chunks.length === 0;
  let startLabel: string = strings.start;
  if (playing) {
    startLabel = strings.pause;
  } else if (index > 0 && !finished) {
    startLabel = strings.resume;
  }

  return (
    <div className="space-y-6">
      <section className="space-y-4">
        <TextAreaField
          hint={strings.textHint}
          id="sr-text"
          label={strings.textLabel}
          onChange={changeText}
          placeholder={strings.textPlaceholder}
          prose
          rows={6}
          value={text}
        />
        <button
          className={BUTTON_SECONDARY}
          onClick={() => changeText(strings.sampleText)}
          type="button"
        >
          {strings.useSample}
        </button>
      </section>

      <section className={`${CARD} space-y-5`}>
        <PillGroup
          label={strings.modeLabel}
          name="sr-mode"
          onChange={(next) => {
            setMode(next);
            relayout(next, size, breakAtPunctuation);
          }}
          options={(["rsvp", "chunks", "pacer"] as const).map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        <p className={HINT}>{strings.modeNotes[mode]}</p>
        {reducedMotion ? <Notice>{strings.reducedMotion}</Notice> : null}
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2">
            <label className={LABEL} htmlFor="sr-wpm">
              {strings.wpmLabel}:{" "}
              <span className="tabular-nums">
                {t(strings.wpmUnit, { wpm: fmt(wpm) })}
              </span>
            </label>
            <input
              className={RANGE}
              id="sr-wpm"
              max={MAX_WPM}
              min={MIN_WPM}
              onChange={(event) => setWpm(Number(event.target.value))}
              step={25}
              type="range"
              value={wpm}
            />
            <p className={HINT}>{strings.wpmHint}</p>
          </div>
          {mode === "rsvp" ? null : (
            <SelectField
              id="sr-size"
              label={strings.sizeLabel}
              onChange={(next) => {
                setSize(next);
                relayout(mode, next, breakAtPunctuation);
              }}
              options={SIZES.map((value) => ({ value, label: value }))}
              value={size}
            />
          )}
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2">
          <Checkbox
            checked={pauses}
            label={strings.pausesLabel}
            onChange={setPauses}
          />
          {mode === "rsvp" ? null : (
            <Checkbox
              checked={breakAtPunctuation}
              label={strings.breakLabel}
              onChange={(next) => {
                setBreakAtPunctuation(next);
                relayout(mode, size, next);
              }}
            />
          )}
        </div>
      </section>

      {empty ? (
        <Notice>{strings.enterText}</Notice>
      ) : (
        <section className="space-y-4">
          {mode === "pacer" ? (
            <PacerText
              label={strings.originalLabel}
              length={current?.length ?? 0}
              start={wordsRead}
              words={words}
            />
          ) : (
            <ReadingStage
              anchor={mode === "rsvp"}
              chunk={current}
              placeholder={strings.placeholder}
            />
          )}
          <div className="flex flex-wrap items-center gap-3">
            <button
              className={BUTTON_PRIMARY}
              onClick={playing ? () => setPlaying(false) : start}
              type="button"
            >
              {startLabel}
            </button>
            <button
              className={BUTTON_SECONDARY}
              disabled={index === 0}
              onClick={back}
              type="button"
            >
              {strings.back}
            </button>
            <button
              className={BUTTON_SECONDARY}
              disabled={index === 0 && !playing}
              onClick={restart}
              type="button"
            >
              {strings.restart}
            </button>
            <span className="text-muted-foreground text-sm tabular-nums">
              {t(strings.progress, {
                current: fmt(Math.min(wordsRead, words.length)),
                total: fmt(words.length),
              })}
            </span>
          </div>
          <div className="space-y-1">
            <label className="sr-only" htmlFor="sr-position">
              {strings.positionLabel}
            </label>
            <input
              className={RANGE}
              id="sr-position"
              max={Math.max(0, chunks.length - 1)}
              min={0}
              onChange={(event) => {
                setIndex(Number(event.target.value));
                setFinished(false);
              }}
              type="range"
              value={Math.min(index, Math.max(0, chunks.length - 1))}
            />
          </div>
          <p className={HINT}>
            {t(strings.runEstimate, {
              words: tPlural(strings.wordCount, locale, estimate.words, {
                count: fmt(estimate.words),
              }),
              time: formatClock(estimate.seconds),
              pace: fmt(estimate.effectiveWpm),
            })}
          </p>
        </section>
      )}

      {finished ? (
        <section aria-live="polite" className={`${CARD} space-y-4`}>
          <h2 className={WIDGET_H2}>{strings.doneHeading}</h2>
          <StatGrid>
            <StatCard label={strings.statWords} value={fmt(estimate.words)} />
            <StatCard
              label={strings.statTime}
              value={formatClock(estimate.seconds)}
            />
            <StatCard emphasis label={strings.statPace} value={fmt(wpm)} />
          </StatGrid>
          <TextAreaField
            hint={strings.recallHint}
            id="sr-recall"
            label={strings.recallLabel}
            onChange={setRecall}
            prose
            rows={5}
            value={recall}
          />
          <button
            className={BUTTON_SECONDARY}
            onClick={() => setShowOriginal((value) => !value)}
            type="button"
          >
            {showOriginal ? strings.hideOriginal : strings.showOriginal}
          </button>
          {showOriginal ? (
            <p className="whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
              {text}
            </p>
          ) : null}
        </section>
      ) : null}

      <p className={HINT}>{strings.caveat}</p>
    </div>
  );
}
